import find from "lodash/find";
import uniqBy from "lodash/uniqBy";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { match, P } from "ts-pattern";

import { useFunnelContext } from "shared/Funnel/FunnelContextProvider";
import { useSwapActiveOrder } from "shared/SwapActiveOrder/useSwapActiveOrder";
import { TrustpilotWidget } from "shared/TrustpilotWidget";
import { useTrackOrder } from "shared/analytics/useTrackOrder";
import { useTrackProduct } from "shared/analytics/useTrackProduct";
import { useTrackSwap } from "shared/analytics/useTrackSwap";
import { useProductPageContext } from "shared/product/ProductPageContext/useProductPageContext";
import { ProductImages } from "shared/product/ProductSection/ProductImages";
import { useCreateNewOrderMutation } from "shared/product/ProductSection/ProductSpecifications/AvailableProduct/create-new-order.mutation.generated";
import { useProductCreateSwapMutation } from "shared/product/ProductSection/ProductSpecifications/AvailableProduct/product-create-swap.mutation.generated";
import { ProductConditions } from "shared/product/ProductSection/ProductSpecifications/shared/ProductConditions";
import { ProductReminderModal } from "shared/product/ProductSection/ProductSpecifications/shared/ProductReminderModal";
import { HorizontalSeparator } from "ui/HorizontalSeparator";
import { StrapiImageData } from "ui/StrapiImage";
import { getCookie, setToken } from "utils/cookies.utils";
import { useModalState } from "utils/useModalState";
import { useI18n } from "utils/with-i18n.utils";

import { PictureThumbnail } from "../shared/PictureThumbnail";
import { StorageCard } from "../shared/StorageCard";

import { ProductOrderButton } from "./ProductOrderButton";
import { ProductPriceDetails } from "./ProductPriceDetails";
import { StickySection } from "./StickySection";

enum Errors {
  MultipleOrdersNotAllowed = "MULTIPLE_ORDERS_NOT_ALLOWED",
}

export const AvailableProduct = () => {
  const i18n = useI18n();
  const { push } = useRouter();
  const { isSwap } = useFunnelContext();
  const { productDetail, family, variants, currentVariant } =
    useProductPageContext();

  const { isOpen, closeModal, openModal } = useModalState();
  const {
    query: { slug },
  } = useRouter();

  const { orderId } = useSwapActiveOrder({});
  const [createNewOrder, { loading: isCreateNewOrderLoading }] =
    useCreateNewOrderMutation();
  const [createSwap, { loading: isCreateSwapLoading }] =
    useProductCreateSwapMutation();
  const { trackCheckoutStarted } = useTrackOrder();
  const { trackProductAdded, trackProductClicked, trackProductViewed } =
    useTrackProduct();
  const { trackSwapFunnelStarted } = useTrackSwap();

  useEffect(() => {
    if (currentVariant?.sku) {
      trackProductViewed(currentVariant.sku);
    }
  }, [currentVariant, trackProductViewed]);

  const basePath = `${isSwap ? "/swap" : ""}/smartphone/${slug}`;
  const isCreatingOrder = isCreateNewOrderLoading || isCreateSwapLoading;
  const subscriptionId = currentVariant?.id;
  const subscriptionSku = currentVariant?.sku;

  const availableColors = uniqBy(
    variants?.filter(
      ({ storageSlug }) => storageSlug === currentVariant?.storageSlug,
    ),
    "colorSlug",
  );
  const availableStorages = uniqBy(
    variants?.filter(
      ({ colorSlug }) => colorSlug === currentVariant?.colorSlug,
    ),
    "storageSlug",
  );

  if (!subscriptionSku) {
    console.error("Missing subscription SKU, ordering is disabled.");
  }

  const isColorInStock = (colorSlug: string) =>
    !!find(variants, {
      colorSlug,
      storageSlug: currentVariant?.storageSlug,
      isInStock: true,
    });

  const isStorageInStock = (storageSlug: string) =>
    !!find(variants, {
      storageSlug,
      colorSlug: currentVariant?.colorSlug,
      isInStock: true,
    });

  const handleCreateNewOrder = () => {
    match({ subscriptionSku, isSwap, orderId })
      .with(
        { isSwap: false, subscriptionSku: P.select(P.not(P.nullish)) },
        async (sku) => {
          try {
            const { data } = await createNewOrder({
              variables: {
                orderAttributes: {
                  sku,
                  attribution: getCookie("_te"),
                },
              },
            });

            const token = data?.result?.session?.token;

            if (token) {
              setToken(data?.result.session.token);
            }

            if (!isSwap && data?.result.orderId) {
              trackProductAdded(sku);
              trackCheckoutStarted(data.result.orderId);
            }

            return push("/commander/recapitulatif");
          } catch (error) {
            return match(error)
              .with({ message: Errors.MultipleOrdersNotAllowed }, () =>
                push("/espace-membre"),
              )
              .otherwise(() => {
                throw error;
              });
          }
        },
      )
      .with(
        {
          isSwap: true,
          subscriptionSku: P.not(P.nullish),
          orderId: P.not(P.nullish),
        },
        async ({ subscriptionSku: sku, orderId: activeOrderId }) => {
          const { data } = await createSwap({
            variables: {
              orderId: activeOrderId,
              nextSubSku: sku,
            },
          });

          if (data?.result.orderId) {
            trackSwapFunnelStarted(data.result.orderId);
          }

          return push("/swap/recapitulatif");
        },
      )
      .run();
  };

  return (
    <>
      {!!subscriptionId && (
        <ProductReminderModal
          isOpen={isOpen}
          closeModal={closeModal}
          subscriptionId={subscriptionId}
        />
      )}
      <div className="flex flex-col gap-4 items-center py-5 px-8 lg:items-start lg:py-8 lg:w-full">
        {/* Title */}
        <h1 className="mt-12 text-2xl text-center lg:mt-0 lg:text-4xl lg:text-left 2xs:text-3xl">
          {i18n.t(
            "product.available.newPhoneName",
            { productName: family.modelName },
            { html: true },
          )}
        </h1>

        {/* Trustpilot */}
        <TrustpilotWidget className="mb-5 lg:-ml-4 lg:w-[420px]" />

        {/* Mobile carousel */}
        <ProductImages className="-mt-5 w-screen min-w-320 lg:hidden" />
        <HorizontalSeparator className="w-screen min-w-320 lg:hidden" />

        {/* Color selector */}
        <p className="text-black">{i18n.t("product.pickColor")}</p>
        <div className="flex flex-wrap gap-2 justify-center">
          {availableColors?.map(({ id, sku, colorSlug, backPicture }) => (
            <Link
              key={id}
              href={`${basePath}/${currentVariant?.storageSlug}-${colorSlug}`}
              onClick={() => !isSwap && trackProductClicked(sku!)}
              replace
              shallow
              scroll={false}
            >
              <PictureThumbnail
                image={backPicture as StrapiImageData}
                isSelected={colorSlug === currentVariant?.colorSlug}
                isDisabled={!isColorInStock(colorSlug)}
              />
            </Link>
          ))}
        </div>

        {/* Storage selector */}
        <p className="text-black">{i18n.t("product.pickStorage")}</p>
        <div className="flex flex-wrap gap-2 justify-center">
          {availableStorages.map(
            ({ id, sku, storage, storageSlug: variantStorageSlug }) => (
              <Link
                key={id}
                href={`${basePath}/${variantStorageSlug}-${currentVariant?.colorSlug}`}
                onClick={() => !isSwap && trackProductClicked(sku!)}
                replace
                shallow
                scroll={false}
              >
                <StorageCard
                  label={storage ?? ""}
                  isSelected={
                    variantStorageSlug === currentVariant?.storageSlug
                  }
                  isDisabled={!isStorageInStock(variantStorageSlug)}
                />
              </Link>
            ),
          )}
        </div>

        {/* Conditions */}
        <p className="text-black">{i18n.t("product.conditions")}</p>
        <ProductConditions />

        {/* Price details */}
        <ProductPriceDetails />

        {/* Order buttons */}
        <ProductOrderButton
          className="w-full max-w-md"
          handleCreateNewOrder={handleCreateNewOrder}
          handleWarnMe={openModal}
          isCreatingOrder={isCreatingOrder}
          subscriptionSku={subscriptionSku}
        />

        {/* Sticky section for mobile */}
        <StickySection
          className="fixed bottom-0 w-screen min-w-320 lg:hidden"
          handleCreateNewOrder={handleCreateNewOrder}
          handleWarnMe={openModal}
          isCreatingOrder={isCreatingOrder}
          subscriptionSku={subscriptionSku}
        />
      </div>
    </>
  );
};
